// /opt/isp_erp/frontend/src/components/tickets/CloseRetiroDialog.jsx

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Chip,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Warning as WarningIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Inventory as InventoryIcon,
    LocationOn as LocationIcon,
    MyLocation as MyLocationIcon,
    PhotoCamera as PhotoIcon,
    Download as DownloadIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import { ticketsAPI } from '../../api/tickets.api';
import axios from '../../api/axios';
import FotoUploader from './FotoUploader';

const CloseRetiroDialog = ({ open, onClose, ticket, onSuccess }) => {
    const [motivoGeneral, setMotivoGeneral] = useState('');
    const [tecnicoId, setTecnicoId] = useState('');
    const [tecnicos, setTecnicos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cargandoTecnicos, setCargandoTecnicos] = useState(false);
    const [error, setError] = useState('');

    // Estados para éxito y PDF
    const [showSuccess, setShowSuccess] = useState(false);
    const [pdfBase64, setPdfBase64] = useState(null);
    const [ticketNumber, setTicketNumber] = useState('');
    const [ticketId, setTicketId] = useState(null);

    // Estados para fotos
    const [fotosBase64, setFotosBase64] = useState([]);

    // Estados para ubicación
    const [ubicacion, setUbicacion] = useState(null);
    const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
    const [errorUbicacion, setErrorUbicacion] = useState('');
    const [usarUbicacion, setUsarUbicacion] = useState(true);

    // Estados para múltiples equipos
    const [equiposInstalados, setEquiposInstalados] = useState([]);
    const [equiposARetirar, setEquiposARetirar] = useState([]);
    const [nuevoEquipo, setNuevoEquipo] = useState({ sn: '', motivo: '' });

    // Estados para materiales
    const [materiales, setMateriales] = useState([]);
    const [nuevoMaterial, setNuevoMaterial] = useState({ material_id: '', cantidad: 1, observaciones: '' });
    const [catalogoMateriales, setCatalogoMateriales] = useState([]);
    const [stockTecnico, setStockTecnico] = useState([]);
    const [cargandoMateriales, setCargandoMateriales] = useState(false);

    // Función para descargar PDF
    const handleDownloadPDF = () => {
        if (!pdfBase64) return;

        try {
            const byteCharacters = atob(pdfBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `retiro_${ticketNumber || ticketId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error descargando PDF:', error);
            setError('Error al descargar el PDF');
        }
    };

    const handleCloseDialog = () => {
        setShowSuccess(false);
        setPdfBase64(null);
        setTicketNumber('');
        setTicketId(null);
        onClose();
    };

    // Función para obtener ubicación
    const obtenerUbicacion = () => {
        if (!navigator.geolocation) {
            setErrorUbicacion('Geolocalización no soportada');
            setUsarUbicacion(false);
            return;
        }

        setObteniendoUbicacion(true);
        setErrorUbicacion('');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const ubicacionData = {
                    latitud: position.coords.latitude,
                    longitud: position.coords.longitude,
                    precision: position.coords.accuracy
                };
                setUbicacion(ubicacionData);
                setObteniendoUbicacion(false);
            },
            (error) => {
                console.error('Error de geolocalización:', error);
                let mensaje = '';
                switch(error.code) {
                    case error.PERMISSION_DENIED:
                        mensaje = 'Permiso denegado. Activa la ubicación en tu dispositivo.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        mensaje = 'No se pudo obtener la ubicación. Verifica tu GPS.';
                        break;
                    case error.TIMEOUT:
                        mensaje = 'Tiempo de espera agotado. Intenta nuevamente.';
                        break;
                    default:
                        mensaje = 'Error al obtener ubicación.';
                }
                setErrorUbicacion(mensaje);
                setObteniendoUbicacion(false);
                setUsarUbicacion(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    useEffect(() => {
        if (open) {
            setShowSuccess(false);
            setPdfBase64(null);
            setTicketNumber('');
            cargarTecnicos();
            cargarMateriales();
            cargarEquiposTicket();
            if (ticket?.assigned_technician_id) {
                cargarStockTecnico(ticket.assigned_technician_id);
                setTecnicoId(ticket.assigned_technician_id);
            }
            if (usarUbicacion) {
                obtenerUbicacion();
            }
        }
    }, [open, ticket]);

    const cargarTecnicos = async () => {
        setCargandoTecnicos(true);
        try {
            const data = await ticketsAPI.getTechnicians();
            setTecnicos(data || []);
        } catch (err) {
            console.error('Error cargando técnicos:', err);
        } finally {
            setCargandoTecnicos(false);
        }
    };

    const cargarMateriales = async () => {
        try {
            const response = await axios.get('/warehouse/materiales');
            setCatalogoMateriales(response.data || []);
        } catch (error) {
            console.error('Error cargando materiales:', error);
        }
    };

    const cargarEquiposTicket = async () => {
        try {
            // Obtener equipos del ticket desde materials_used
            if (ticket?.materials_used && ticket.materials_used.equipos) {
                setEquiposInstalados(ticket.materials_used.equipos);
            } else if (ticket?.new_equipment_sn) {
                setEquiposInstalados([{ sn: ticket.new_equipment_sn, tipo: 'ONU' }]);
            } else if (ticket?.old_equipment_sn) {
                setEquiposInstalados([{ sn: ticket.old_equipment_sn, tipo: 'EQUIPO' }]);
            }
        } catch (error) {
            console.error('Error cargando equipos:', error);
        }
    };

    const cargarStockTecnico = async (tecnicoId) => {
        setCargandoMateriales(true);
        try {
            const response = await axios.get(`/warehouse/materiales/stock-tecnico/${tecnicoId}`);
            setStockTecnico(response.data || []);
        } catch (error) {
            console.error('Error cargando stock:', error);
        } finally {
            setCargandoMateriales(false);
        }
    };

    const agregarEquipo = () => {
        if (!nuevoEquipo.sn) {
            setError('Seleccione un equipo');
            return;
        }
        if (!nuevoEquipo.motivo) {
            setError('Ingrese el motivo del retiro');
            return;
        }
        if (equiposARetirar.some(e => e.sn === nuevoEquipo.sn)) {
            setError('Este equipo ya ha sido agregado');
            return;
        }
        setEquiposARetirar([...equiposARetirar, { ...nuevoEquipo }]);
        setNuevoEquipo({ sn: '', motivo: '' });
        setError('');
    };

    const eliminarEquipo = (index) => {
        setEquiposARetirar(equiposARetirar.filter((_, i) => i !== index));
    };

    // ✅ CORREGIDO: agregarMaterial con logs y mejor manejo de estado
    const agregarMaterial = () => {
        if (!nuevoMaterial.material_id) {
            setError('Seleccione un material');
            return;
        }
        if (!nuevoMaterial.cantidad || nuevoMaterial.cantidad < 1) {
            setError('Ingrese una cantidad válida');
            return;
        }

        const materialInfo = catalogoMateriales.find(m => m.id === nuevoMaterial.material_id);

        // ✅ Crear el nuevo material
        const nuevoMaterialObj = {
            material_id: nuevoMaterial.material_id,
            nombre: materialInfo?.nombre || 'Material',
            cantidad: nuevoMaterial.cantidad,
            observaciones: nuevoMaterial.observaciones || ''
        };

        console.log('✅ Agregando material:', nuevoMaterialObj);
        console.log('📦 Materiales antes:', materiales);

        // ✅ Actualizar el estado correctamente con función de actualización
        setMateriales(prevMateriales => {
            const nuevos = [...prevMateriales, nuevoMaterialObj];
            console.log('📦 Materiales después:', nuevos);
            return nuevos;
        });

        // ✅ Limpiar el formulario
        setNuevoMaterial({ material_id: '', cantidad: 1, observaciones: '' });
        setError('');
    };

    const eliminarMaterial = (index) => {
        setMateriales(materiales.filter((_, i) => i !== index));
    };

    // ✅ CORREGIDO: handleSubmit con validación de materiales y logs
    const handleSubmit = async () => {
        if (!tecnicoId) {
            setError('Debe seleccionar un técnico');
            return;
        }
        if (equiposARetirar.length === 0) {
            setError('Debe seleccionar al menos un equipo a retirar');
            return;
        }

        // ✅ VALIDAR QUE HAYA MATERIALES SELECCIONADOS
        if (materiales.length === 0) {
            setError('Debe seleccionar al menos un material a devolver');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // ✅ Preparar ubicación
            let ubicacionData = null;
            if (usarUbicacion && ubicacion) {
                ubicacionData = {
                    latitud: ubicacion.latitud,
                    longitud: ubicacion.longitud,
                    precision: ubicacion.precision
                };
            }

            // ✅ LOG PARA VERIFICAR MATERIALES ANTES DE ENVIAR
            console.log('📦 MATERIALES EN EL STATE:', materiales);
            console.log('📦 MATERIALES DETALLE:', JSON.stringify(materiales, null, 2));
            console.log('📦 CANTIDAD DE MATERIALES:', materiales.length);

            // ✅ Preparar materiales para enviar (formato que espera el backend)
            const materialesPayload = materiales.map(m => ({
                nombre: m.nombre,
                cantidad: m.cantidad,
                observaciones: m.observaciones || ''
            }));

            console.log('📦 MATERIALES PAYLOAD:', materialesPayload);

            // ✅ Preparar equipos para enviar
            const equiposPayload = equiposARetirar.map(e => ({
                sn: e.sn,
                motivo: e.motivo || motivoGeneral || ticket?.description || 'Retiro de equipo'
            }));

            console.log('=== ENVIANDO PAYLOAD ===');
            console.log('equipos:', equiposPayload);
            console.log('materiales:', materialesPayload);
            console.log('motivo_retiro:', motivoGeneral);
            console.log('fotos:', fotosBase64.length);
            console.log('ubicacion:', ubicacionData);

            const payload = {
                equipos: equiposPayload,
                materiales: materialesPayload,
                motivo_retiro: motivoGeneral || "",
                fotos: fotosBase64,
                ubicacion: ubicacionData
            };

            console.log('PAYLOAD FINAL:', JSON.stringify(payload, null, 2));

            // ✅ Verificar que el payload tenga materiales
            if (payload.materiales.length === 0) {
                console.warn('⚠️ El payload tiene 0 materiales!');
            }

            // ✅ Cerrar ticket con el payload completo
            const result = await ticketsAPI.closeRetiroTicket(ticket.id, payload);

            // ✅ Verificar si se generó PDF
            if (result.pdf_generado && result.pdf_base64) {
                setPdfBase64(result.pdf_base64);
                setTicketNumber(result.ticket || ticket.ticket_number);
                setTicketId(ticket.id);
            }

            setShowSuccess(true);
            if (onSuccess) onSuccess();

            // ✅ Cerrar el diálogo después de un breve delay
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (err) {
            console.error('Error completo:', err);
            console.error('Response:', err.response);
            setError(err.response?.data?.detail || 'Error al cerrar el ticket');
            setShowSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    // Pantalla de éxito
    if (showSuccess) {
        return (
            <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <CheckIcon sx={{ fontSize: 60, color: 'success.main' }} />
                    <Typography variant="h5" color="success.main" sx={{ mt: 1 }}>
                        ¡Retiro Completado!
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            Ticket <strong>{ticketNumber || ticket?.ticket_number}</strong> cerrado exitosamente.
                        </Typography>
                        {pdfBase64 && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPDF}
                                sx={{ mt: 3, mb: 2, px: 4 }}
                                fullWidth
                            >
                                📄 Descargar PDF del Retiro
                            </Button>
                        )}
                        {!pdfBase64 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                El PDF se generará automáticamente.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button variant="outlined" onClick={handleCloseDialog} size="large">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (!ticket) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Completar Retiro
                <Typography variant="body2" color="text.secondary">
                    Ticket: {ticket.ticket_number} - {ticket.title}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* FOTOS */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PhotoIcon color="primary" />
                        Evidencia Fotográfica
                    </Typography>
                    <FotoUploader onFotosChange={setFotosBase64} maxFotos={9} />
                </Box>

                {/* UBICACIÓN */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon color="primary" />
                            Ubicación del Retiro
                        </Typography>
                        <FormControlLabel
                            control={<Switch checked={usarUbicacion} onChange={(e) => setUsarUbicacion(e.target.checked)} size="small" />}
                            label="Guardar ubicación"
                        />
                    </Box>
                    {usarUbicacion && (
                        <>
                            {!ubicacion && !obteniendoUbicacion && !errorUbicacion && (
                                <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={obtenerUbicacion} fullWidth>
                                    Obtener ubicación actual
                                </Button>
                            )}
                            {obteniendoUbicacion && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Obteniendo ubicación...</Typography>
                                </Box>
                            )}
                            {errorUbicacion && (
                                <Alert severity="warning" size="small" sx={{ mt: 1 }}>{errorUbicacion}</Alert>
                            )}
                            {ubicacion && (
                                <Alert severity="success" sx={{ mt: 1 }} icon={<MyLocationIcon />}>
                                    <Typography variant="caption" display="block">
                                        Lat: {ubicacion.latitud.toFixed(6)} | Lng: {ubicacion.longitud.toFixed(6)}
                                    </Typography>
                                    {ubicacion.precision && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Precisión: ±{Math.round(ubicacion.precision)}m
                                        </Typography>
                                    )}
                                </Alert>
                            )}
                        </>
                    )}
                </Paper>

                {/* Equipos Instalados */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Equipos Instalados
                    </Typography>
                    {equiposInstalados.length === 0 ? (
                        <Alert severity="info">No hay equipos registrados en este ticket</Alert>
                    ) : (
                        <Box>
                            {equiposInstalados.map((eq, idx) => (
                                <Chip key={idx} label={`${eq.sn} (${eq.tipo || 'ONU'})`} sx={{ mr: 1, mb: 1 }} />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Equipos a Retirar */}
                <Typography variant="h6" gutterBottom>
                    Equipos a Retirar
                </Typography>

                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Equipo</InputLabel>
                                <Select
                                    value={nuevoEquipo.sn}
                                    onChange={(e) => setNuevoEquipo({ ...nuevoEquipo, sn: e.target.value })}
                                    label="Equipo"
                                >
                                    {equiposInstalados.map(eq => (
                                        <MenuItem key={eq.sn} value={eq.sn}>
                                            {eq.sn} ({eq.tipo || 'ONU'})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                label="Motivo del Retiro"
                                size="small"
                                value={nuevoEquipo.motivo}
                                onChange={(e) => setNuevoEquipo({ ...nuevoEquipo, motivo: e.target.value })}
                                placeholder="Ej: Cliente canceló servicio, equipo dañado"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button fullWidth variant="contained" onClick={agregarEquipo} startIcon={<AddIcon />} disabled={!nuevoEquipo.sn || !nuevoEquipo.motivo}>
                                Agregar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {equiposARetirar.length > 0 && (
                    <List sx={{ mb: 2 }}>
                        <Divider />
                        {equiposARetirar.map((eq, idx) => (
                            <React.Fragment key={idx}>
                                <ListItem>
                                    <ListItemText primary={eq.sn} secondary={`Motivo: ${eq.motivo}`} />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => eliminarEquipo(idx)} size="small" color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>
                )}

                {/* Materiales a Devolver */}
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Materiales a Devolver
                </Typography>

                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Material</InputLabel>
                                <Select
                                    value={nuevoMaterial.material_id}
                                    onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, material_id: e.target.value })}
                                    label="Material"
                                >
                                    {catalogoMateriales.map(m => {
                                        const stock = stockTecnico.find(s => s.material_id === m.id);
                                        return (
                                            <MenuItem key={m.id} value={m.id}>
                                                {m.nombre} ({stock?.cantidad || 0} {m.unidad} disponibles)
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Cantidad"
                                size="small"
                                value={nuevoMaterial.cantidad}
                                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, cantidad: parseFloat(e.target.value) || 1 })}
                                inputProps={{ min: 1, step: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Observaciones"
                                size="small"
                                value={nuevoMaterial.observaciones}
                                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, observaciones: e.target.value })}
                                placeholder="Estado del material, etc."
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Button fullWidth variant="contained" onClick={agregarMaterial} startIcon={<AddIcon />} disabled={!nuevoMaterial.material_id}>
                                Agregar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {materiales.length > 0 && (
                    <List sx={{ mb: 2 }}>
                        <Divider />
                        {materiales.map((mat, idx) => (
                            <React.Fragment key={idx}>
                                <ListItem>
                                    <ListItemText
                                        primary={`${mat.nombre} x${mat.cantidad}`}
                                        secondary={mat.observaciones || 'Sin observaciones'}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => eliminarMaterial(idx)} size="small" color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>
                )}

                {/* Técnico y Motivo General */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Técnico que realiza el retiro *</InputLabel>
                    <Select
                        value={tecnicoId}
                        onChange={(e) => setTecnicoId(e.target.value)}
                        label="Técnico que realiza el retiro *"
                        disabled={cargandoTecnicos}
                    >
                        <MenuItem value="">Seleccione un técnico</MenuItem>
                        {tecnicos.map((tech) => (
                            <MenuItem key={tech.id} value={tech.id}>
                                {tech.full_name || tech.username} - {tech.phone || 'Sin teléfono'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Motivo General (opcional)"
                    multiline
                    rows={2}
                    value={motivoGeneral}
                    onChange={(e) => setMotivoGeneral(e.target.value)}
                    placeholder="Observaciones adicionales sobre el retiro..."
                    sx={{ mb: 2 }}
                />

                {cargandoMateriales && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="caption">Cargando stock del técnico...</Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Información:</strong> Al cerrar este ticket, los equipos cambiarán a estado <strong>RETIRO_PENDIENTE</strong>,
                    se devolverán los materiales al stock y quedarán bajo responsabilidad del técnico para su devolución a bodega.
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="warning"
                    disabled={loading || !tecnicoId || equiposARetirar.length === 0 || materiales.length === 0}
                    startIcon={loading ? <CircularProgress size={20} /> : <WarningIcon />}
                >
                    {loading ? 'Cerrando...' : 'Completar Retiro'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CloseRetiroDialog;
